import { useContext, useRef, useState } from 'react';
import { Confirm, useAuthenticated, useGetList, useUpdate } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import useDeleteResource from '@app/hooks/useDeleteResource';
import { ActivateIcon } from '@app/icons/ActivateIcon';
import { DeactivateIcon } from '@app/icons/DeactivateIcon';
import { DeleteIcon } from '@app/icons/DeleteIcon';
import { EditIcon } from '@app/icons/EditIcon';
import authService from '@app/services/auth/authService';
import { getFieldOnMainLanguage } from '@app/utils/languages';
import { AppContext } from 'contexts';

import { CustomActionList } from '@app/components/common/CustomActionList';
import { ColumnTypes, GenericObject, GenericObjectWithActions } from '@app/components/common/Table/DefaultTable/types';

export const ListUsers = () => {
  const { handleChangeLoading } = useContext(AppContext);

  const [confirmActivateOpen, setConfirmActivateOpen] = useState(false);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);

  const [update, { isLoading: isUpdateLoading }] = useUpdate();
  const actionedResource = useRef<GenericObject>();

  const navigate = useNavigate();

  /**
   * List[X] pages require manual auth check for some reason.
   * This involves the `checkAuth` method in auth.ts.
   */
  useAuthenticated();

  const onActivate = (row: GenericObject) => {
    actionedResource.current = row;
    setConfirmActivateOpen(true);
  };

  const onDeactivate = (row: GenericObject) => {
    actionedResource.current = row;
    setConfirmDeactivateOpen(true);
  };

  const activate = () => {
    update('users', {
      id: actionedResource.current!.id as string,
      data: {
        ...actionedResource.current,
        active: true,
      },
      previousData: actionedResource.current,
    });
    // TODO error handle the update
    setConfirmActivateOpen(false);
  };

  const { onDelete, isDeleteConfirmationOpen, handleConfirmDelete, handleDeleteDialogClose, isDeleteLoading } =
    useDeleteResource('users', 'user');

  const deactivate = () => {
    update('users', {
      id: actionedResource.current!.id as string,
      data: {
        ...actionedResource.current,
        active: false,
      },
      previousData: actionedResource.current,
    });
    // TODO error handle the update
    setConfirmDeactivateOpen(false);
  };

  const onEdit = (row: GenericObject) => {
    navigate(`/users/${row.id}`);
  };

  // @ts-ignore `data` definitely exists
  const { isLoading, data } = useGetList('users', {
    sort: { field: 'id', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  });

  let rowsWithActions: GenericObjectWithActions[] | undefined = undefined;

  if (data) {
    // Save to use getUserEmail() here, as we would not
    // be able to get this far in the code if the user was not
    // authenticated
    const currentUserEmail = authService.getUserEmail();

    const commonActions = [
      { id: 'edit', icon: EditIcon, action: onEdit },
      { id: 'delete', icon: DeleteIcon, action: onDelete },
    ];

    rowsWithActions = data.map((row: GenericObjectWithActions) => {
      if (row.active === true) {
        // Active user
        // Don't show any actions for currently logged-in user!
        if (row.email !== currentUserEmail) {
          row.actions = [...commonActions, { id: 'deactivate', icon: DeactivateIcon, action: onDeactivate }];
        }
      } else {
        // Inactive user
        row.actions = [...commonActions, { id: 'activate', icon: ActivateIcon, action: onActivate }];
      }
      return row;
    });
  }

  const columns = [
    {
      label: 'NAME',
      key: 'name',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => getFieldOnMainLanguage(row?.name),
    },
    {
      label: 'EMAIL',
      key: 'email',
      format: (row: GenericObject) => row?.email as string,
      type: ColumnTypes.DEFAULT,
    },
    {
      label: 'ROLE',
      key: 'role',
      format: (row: GenericObject) => {
        // Temporary prettification
        // TODO adjust once we start implementing Role-Based Access Control
        switch (row?.role) {
          case 'ADMIN_USER':
            return 'Admin User';
          case 'SUPER_ADMIN_USER':
            return 'Super Admin User';
          case 'READONLY_USER':
            return 'Read-Only User';
          default:
            return 'INVALID ROLE';
        }
      },
      type: ColumnTypes.DEFAULT,
    },
  ];

  return (
    <>
      <CustomActionList
        handleChangeLoading={handleChangeLoading}
        columns={columns}
        resource="users"
        title="Users"
        isLoading={isLoading}
        rowsWithActions={rowsWithActions}
      />
      <Confirm
        isOpen={confirmActivateOpen}
        loading={isUpdateLoading}
        title={`Activate user`}
        content={`Are you sure you want to activate this user?`}
        onConfirm={() => {
          activate();
        }}
        onClose={() => {
          setConfirmActivateOpen(false);
        }}
      />
      <Confirm
        isOpen={confirmDeactivateOpen}
        loading={isUpdateLoading}
        title={`Deactivate user`}
        content={`Are you sure you want to deactivate this user?`}
        onConfirm={() => {
          deactivate();
        }}
        onClose={() => {
          setConfirmDeactivateOpen(false);
        }}
      />
      <Confirm
        isOpen={isDeleteConfirmationOpen}
        loading={isDeleteLoading}
        title={`Delete user`}
        content={'Are you sure you want to delete this user?'}
        onConfirm={handleConfirmDelete}
        cancel="Cancel"
        confirm="Delete"
        onClose={handleDeleteDialogClose}
      />
    </>
  );
};
